/*
 * Copyright (c) 2021 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Wednesday, September 29th 2021, 7:20:28 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import Button, { ButtonProps } from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import LoginIcon from "@mui/icons-material/Login";
import SettingsIcon from "@mui/icons-material/Settings";
import { COLORSCHEME } from "theme/styles";
import CircularProgress from "@mui/material/CircularProgress";
import { Link, LinkProps } from "react-router-dom";
import IconButton, { IconButtonProps } from "@mui/material/IconButton";
import Tooltip, { TooltipProps } from "@mui/material/Tooltip";
import { ArrowLeft } from "react-feather";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import LaunchIcon from "@mui/icons-material/Launch";
import Fab, { FabProps } from "@mui/material/Fab";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";

export interface OSActionButtonProps extends ButtonProps, Partial<Pick<LinkProps, "to">> {
  actionStyle?:
    | "add"
    | "save"
    | "delete"
    | "edit"
    | "manage-accounts"
    | "login"
    | "back"
    | "choose"
    | "launch"
    | "custom"
    | "configure";
  customIcon?: React.ReactNode;
  buttonType?: "normal" | "floating";
}

export const OSActionButton = ({ actionStyle, customIcon, buttonType, onClick, ...props }: OSActionButtonProps) => {
  let icon: React.ReactNode;

  switch (actionStyle) {
    case "add":
      icon = <AddIcon />;
      break;
    case "edit":
      icon = <EditIcon />;
      break;
    case "save":
      icon = <SaveIcon />;
      break;
    case "delete":
      icon = <DeleteIcon />;
      break;
    case "manage-accounts":
      icon = <ManageAccountsIcon />;
      break;
    case "login":
      icon = <LoginIcon />;
      break;
    case "back":
      icon = <ArrowBackIcon />;
      break;
    case "choose":
      icon = <ArrowDropDownIcon />;
      break;
    case "launch":
      icon = <LaunchIcon />;
      break;
    case "configure":
      icon = <SettingsIcon />;
      break;
    case "custom":
      icon = customIcon;
      break;
  }

  const color = actionStyle === "delete" ? "error" : "primary";

  return (
    <ButtonLinkWrapper to={props.to}>
      {buttonType === "floating" ? (
        <Fab
          color="primary"
          size={props.size ?? "small"}
          variant="extended"
          onClick={onClick}
          className={props.className}
          sx={props.sx}
        >
          {icon}
        </Fab>
      ) : (
        <Button
          {...props}
          size={props.size ?? "small"}
          variant={props.variant ?? "outlined"}
          color={props.color ?? color}
          startIcon={icon}
          onClick={onClick}
        >
          {props.children}
        </Button>
      )}
    </ButtonLinkWrapper>
  );
};

export const ButtonLinkWrapper = ({ to, children }) => {
  return (
    <React.Fragment>
      {to ? <Link to={to}>{children}</Link> : <React.Fragment>{children}</React.Fragment>}
    </React.Fragment>
  );
};

interface OSLoadingButtonProps extends OSActionButtonProps {
  isLoading: boolean;
}

export const OSLoadingButton = ({ isLoading, ...props }: OSLoadingButtonProps) => {
  return (
    <OSActionButton disabled={isLoading} {...props}>
      {isLoading && (
        <React.Fragment>
          <CircularProgress size={24} color="secondary" />
          <span>&nbsp;&nbsp;</span>
        </React.Fragment>
      )}
      {props.children}
    </OSActionButton>
  );
};

const successButtonBg = {
  bgcolor: COLORSCHEME.success,
  "&:hover": {
    bgcolor: COLORSCHEME.success,
  },
};

export const SuccessCheckButton = (props: FabProps) => {
  const { size, sx } = props;

  return (
    <Fab
      color="primary"
      size={size ?? "small"}
      sx={{
        ...successButtonBg,
        mr: 1,
        ...sx,
      }}
      variant="extended"
    >
      <CheckIcon />
      {props.children}
    </Fab>
  );
};

const errrorButtonBg = {
  bgcolor: COLORSCHEME.error,
  "&:hover": {
    bgcolor: COLORSCHEME.error,
  },
};

export const ErrorXButton = (props: FabProps) => {
  const { size, sx } = props;

  return (
    <Fab
      color="primary"
      size={size ?? "small"}
      sx={{
        ...errrorButtonBg,
        mr: 1,
        ...sx,
      }}
      variant="extended"
    >
      <CloseIcon />
      {props.children}
    </Fab>
  );
};

export const OSBackButton = (props: Pick<IconButtonProps, "onClick">) => (
  <IconButton aria-label="back" onClick={props.onClick}>
    <ArrowLeft color={COLORSCHEME.primary} size="18" />
  </IconButton>
);

interface OSIconButtonProps extends Pick<IconButtonProps, "color" | "onClick"> {
  icon?: React.ReactNode;
  toolTipText?: TooltipProps["title"];
}

export const OSIconButton = ({ color, onClick, icon, toolTipText = "" }: OSIconButtonProps) => (
  <Tooltip title={toolTipText}>
    <IconButton onClick={onClick} color={color}>
      {icon ?? <EditIcon sx={color ? null : { color: COLORSCHEME.secondaryVariant }} />}
    </IconButton>
  </Tooltip>
);

export const OSDeleteButton = (props: Omit<OSIconButtonProps, "icon">) => {
  return <OSIconButton icon={<DeleteIcon />} color="error" {...props} />;
};

export const EditButtonIcon = ({ onClick }: Pick<OSIconButtonProps, "onClick">) => {
  return (
    <IconButton onClick={onClick}>
      <EditIcon color="primary" />
    </IconButton>
  );
};
